import { useEffect } from 'react';
import { Card } from '@/components/ui';

// Small confirmation dialog for destructive actions (per docs/03: confirm before
// destructive). Stone + amber, backdrop, Esc to cancel. Confirm button is amber
// by default or red when `destructive`.
interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onCancel();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-black/60 p-4"
      onClick={onCancel}
      role="presentation"
    >
      <Card
        variant="panel"
        padding="lg"
        className="w-full max-w-sm space-y-4"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="space-y-1.5">
          <h2 className="text-base text-stone-100">{title}</h2>
          <p className="text-sm leading-relaxed text-stone-400">{message}</p>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-stone-700 px-3 py-1.5 text-sm text-stone-300 hover:border-stone-600 disabled:opacity-40"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={
              destructive
                ? 'rounded-md bg-red-700 px-3 py-1.5 text-sm font-medium text-stone-50 hover:bg-red-600 disabled:opacity-40'
                : 'rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-stone-950 hover:bg-amber-700 disabled:opacity-40'
            }
          >
            {confirmLabel}
          </button>
        </div>
      </Card>
    </div>
  );
}
